import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { useListingActions } from '../../../hooks/useListingAdmin';
import { ListingMessage } from '../../../types';
import { activeMessages, pickLang } from '../../../utils/listingDisplay';
import { getIsraelToday, ISRAEL_TIME_ZONE } from '../../../utils/israelTime';
import {
  endMessageNow,
  expiredMessages,
  LISTING_LIMITS,
  maxMessageExpiry,
  MESSAGE_DURATIONS,
  MessageDuration,
  messageExpiry,
  newMessage,
  trimMessages,
} from '../../../utils/listingEdit';
import { BilingualField, ErrorList, SectionTitle } from './formFields';
import type { TabProps } from './ListingEditor';

export default function MessagesTab({ listing }: TabProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const { updateListing, saving } = useListingActions();
  const [textHe, setTextHe] = useState('');
  const [textEn, setTextEn] = useState('');
  const [duration, setDuration] = useState<MessageDuration>('1w');
  const [untilDate, setUntilDate] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  const [posted, setPosted] = useState(false);

  const nowIso = new Date().toISOString();
  const messages = listing.messages ?? [];
  const active = activeMessages(messages, nowIso);
  const ended = expiredMessages(messages, nowIso);

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(isHebrew ? 'he-IL' : 'en-GB', {
      timeZone: ISRAEL_TIME_ZONE,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));

  const today = getIsraelToday();
  const maxDate = getIsraelToday(new Date(maxMessageExpiry(nowIso)));
  const previewEnd = duration === 'date' && !untilDate ? null : messageExpiry(nowIso, duration, untilDate || undefined);

  const write = async (next: ListingMessage[]) => {
    setFailed(false);
    try {
      await updateListing(listing.id, { messages: next });
      return true;
    } catch {
      setFailed(true);
      return false;
    }
  };

  const post = async () => {
    const problems: string[] = [];
    if (!textHe.trim() && !textEn.trim()) problems.push('messageTextRequired');
    if (duration === 'date' && !untilDate) problems.push('messageDateRequired');
    setErrors(problems);
    if (problems.length) return;
    const message = newMessage({ textHe, textEn }, new Date().toISOString(), duration, untilDate || undefined);
    if (await write(trimMessages([message, ...messages], message.startsAt))) {
      setTextHe('');
      setTextEn('');
      setUntilDate('');
      setPosted(true);
      window.setTimeout(() => setPosted(false), 2500);
    }
  };

  const endNow = async (id: string) => {
    if (!window.confirm(t('directory.admin.messages.confirmEnd'))) return;
    await write(endMessageNow(messages, id, new Date().toISOString()));
  };

  const remove = async (id: string) => {
    if (!window.confirm(t('directory.admin.messages.confirmDelete'))) return;
    await write(messages.filter((m) => m.id !== id));
  };

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <SectionTitle>{t('directory.admin.messages.newTitle')}</SectionTitle>
        <p className="text-sm text-ash">{t('directory.admin.messages.hint')}</p>
        <BilingualField
          labelHe={`${t('directory.admin.messages.text')} (${t('directory.admin.hebrew')})`}
          labelEn={`${t('directory.admin.messages.text')} (${t('directory.admin.english')})`}
          valueHe={textHe}
          valueEn={textEn}
          onChangeHe={setTextHe}
          onChangeEn={setTextEn}
          multiline
          maxLength={LISTING_LIMITS.noteLength}
          placeholderHe={t('directory.admin.messages.placeholderHe')}
          placeholderEn={t('directory.admin.messages.placeholderEn')}
        />

        <fieldset>
          <legend className="text-sm font-medium text-ink mb-1">{t('directory.admin.messages.showFor')}</legend>
          <div className="flex flex-wrap gap-2">
            {MESSAGE_DURATIONS.map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={duration === d}
                onClick={() => setDuration(d)}
                className={`min-h-[44px] px-3 rounded-full border text-sm transition-colors ${
                  duration === d ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink/20 hover:border-ink/50'
                }`}
              >
                {t(`directory.admin.messages.duration.${d}`)}
              </button>
            ))}
          </div>
        </fieldset>

        {duration === 'date' && (
          <div>
            <label htmlFor="message-until" className="block text-sm font-medium text-ink mb-1">
              {t('directory.admin.messages.untilDate')}
            </label>
            <input
              id="message-until"
              type="date"
              min={today}
              max={maxDate}
              value={untilDate}
              onChange={(e) => setUntilDate(e.target.value)}
              className="min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
            />
          </div>
        )}

        {previewEnd && (
          <p className="text-sm text-ash">{t('directory.admin.messages.showsUntil', { date: formatDate(previewEnd) })}</p>
        )}
        <p className="text-xs text-ash">{t('directory.admin.messages.maxNote')}</p>

        <ErrorList errors={errors} />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={post}
            disabled={saving}
            className="min-h-[44px] px-5 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-40"
          >
            {t('directory.admin.messages.post')}
          </button>
          <span role="status" className="text-sm">
            {failed ? (
              <span className="text-danger-600">{t('directory.admin.saveFailed')}</span>
            ) : posted ? (
              <span className="text-ink">{t('directory.admin.messages.posted')}</span>
            ) : null}
          </span>
        </div>
      </section>

      <section className="space-y-3 pt-2 border-t border-ink/10">
        <SectionTitle>{t('directory.admin.messages.activeTitle')}</SectionTitle>
        {active.length === 0 ? (
          <p className="text-sm text-ash">{t('directory.admin.messages.noActive')}</p>
        ) : (
          <ul className="space-y-2">
            {active.map((m) => (
              <li key={m.id} className="rounded-[16px] border border-ink/10 p-3 space-y-2">
                <p className="text-sm text-ink whitespace-pre-line break-words">{pickLang(m.textEn, m.textHe, isHebrew)}</p>
                <p className="text-xs text-ash">{t('directory.admin.messages.until', { date: formatDate(m.expiresAt) })}</p>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => endNow(m.id)}
                  className="min-h-[44px] px-3 rounded-[10px] border border-ink/20 text-sm text-ink hover:border-ink/50 disabled:opacity-40"
                >
                  {t('directory.admin.messages.endNow')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {ended.length > 0 && (
        <details className="pt-2 border-t border-ink/10">
          <summary className="min-h-[44px] flex items-center cursor-pointer text-base font-semibold text-ink">
            {t('directory.admin.messages.endedTitle', { count: ended.length })}
          </summary>
          <ul className="space-y-2 mt-2">
            {ended.map((m) => (
              <li key={m.id} className="rounded-[16px] border border-ink/10 p-3 flex items-start gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm text-ink/70 whitespace-pre-line break-words">{pickLang(m.textEn, m.textHe, isHebrew)}</p>
                  <p className="text-xs text-ash">{t('directory.admin.messages.ended', { date: formatDate(m.expiresAt) })}</p>
                </div>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => remove(m.id)}
                  aria-label={t('common.delete')}
                  className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-[10px] text-danger-600 hover:opacity-70 disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
